import React, { useContext, useState, useEffect, useRef } from 'react'
import TreeKeys from './SetTreeKeys'
import { DashboardContext } from 'live/context/dashboard'
import { formatTree, checkingConfigsParams } from '../utils/utils'
import { FooterActions, ModalBody, SpanContainerStyled } from './DropdownStyles'
import LiveModal from 'live/components/ui/Modal'
import LiveLoading from 'live/components/ui/Loading'
import i18n from 'live/services/i18n'

import { get } from 'lodash'
import dashboardService from 'live/services/dashboard'
import { SpanPickerContext } from '../utils/initialStates'
import { WidgetEditorContext } from 'live/context/widget-editor'
import TimespanSelect from './timespanSelect'
import { ExampleFormattedTree, unformattedTreeWellTest3 } from '../utils/exampleData'

/**
 * This function gets the tree from the backend
 * @param assetName {String} - the selected asset
 * @param loadingSelectedUnitSet {boolean} - informantion about loading the tree
 * @param selectedUnitSet {id: boolean} - the unitset chosen in live
 * @returns {Promise} - when the fetching part is done it returns the tree or the error
 */

export const getTreeUnformatted = (
    assetName: string,
    selectedUnitSet: { id: number },
    loadingSelectedUnitSet: boolean,
    setFetchingTree: Function,
    setError: Function
) => {
    setFetchingTree(true)
    setError(false)
    return fetch(
        `/services/plugin-drilling-operations-span-picker/tree/${encodeURIComponent(assetName)}${
            !loadingSelectedUnitSet && selectedUnitSet?.id ? `?selectedUnitSetId=${selectedUnitSet?.id}` : ''
        }/plugin-drilling-operations-span-picker`
    )
        .then(r => {
            setFetchingTree(false)
            return r.json()
        })
        .then(r => {
            r.assetName = assetName
            return r
        })
        .catch(error => {
            setFetchingTree(false)
            setError(true)
            console.log('Error on fetching tree: ', error)
            return Promise.reject()
        })
}

/**
 * This function gets the asset name from the lookup asset_name_event_type (usually passed by the dashboard context)
 * @param filter {String} - a stringified version of the dashboard filters' object
 * @returns {String} - the name of the first asset on the lookup asset_name_event_type
 */

export const getAssetName = (filter: string, assetThroughWidgetContext): string => {
    let res = ''
    const lookup = JSON.parse(filter || '{}')
    if (lookup['asset_name_event_type']) {
        res = lookup['asset_name_event_type'][0]
    } else if (assetThroughWidgetContext?.length > 0) {
        res = assetThroughWidgetContext[0]
    }
    return res
}

/**
 * This function stores a previous state of a variable
 * @param value {any} - variable
 * @returns  {any} - previous state
 */

function usePrevious(value: any) {
    const ref = useRef()
    useEffect(() => {
        ref.current = value
    })
    return ref.current
}

/**
 * This function returns if the variable has changed its value
 * @param val {any} - the tracked variable
 * @returns {boolean}
 */

const useHasChanged = (val: any) => {
    const prevVal = usePrevious(val)
    return prevVal !== val
}

const OperationsPickerConfigPath = dashboardService.getSpanPickerConfigPath('drilling-operations-span-picker')

/**
 * This component catches the operation picker configs, fetches the tree (refetches after changing asset),
 * and provides a formatted tree (accordingly to its configs) to its child.
 *
 * @param open {boolean} - defines if it should show a component
 * @param toggleOpen {Function} - a function that can change the value of open
 * @returns {JSX.Element} - a child that will return a JSX Element
 */

const OperationsTreeInitializer = ({
    open,
    toggleOpen,
    context,
    changeSpanPickerConfig
}: {
    open: boolean
    toggleOpen: Function
    context: string
    changeSpanPickerConfig: any
}): JSX.Element | null => {
    const dashboardContext: any = useContext(DashboardContext)
    const widgetContext = useContext(WidgetEditorContext)

    const [asset, setAsset] = useState<string>(
        getAssetName(
            dashboardContext?.params?.filter,
            widgetContext?.filters?.current?.lookupValues?.asset_name_event_type
        )
    )
    const [fetchingTree, setFetchingTree] = useState(false)
    const [error, setError] = useState(false)
    const [innerOpen, setInnerOpen] = useState(false)
    const OperationsPickerConfigs: OperationsPickerConfigsProps = checkingConfigsParams(
        dashboardContext.params,
        get(dashboardContext, 'dashboard.' + OperationsPickerConfigPath, {})
    )
    const [operationsPickerConfigsState, setOperationsPickerConfigsState] = useState<OperationsPickerConfigsProps>(
        OperationsPickerConfigs
    )
    const [formattedTree, setFormattedTree] = useState<Array<interventionDataTypes>>([])
    // use for test
    // const [formattedTree, setFormattedTree] = useState<Array<interventionDataTypes>>(formatTree(unformattedTreeWellTest3.tree, OperationsPickerConfigs))

    const refAsset = useRef(asset)

    const hasAssetChanged = useHasChanged(asset)
    const haveConfigsChanged = useHasChanged(JSON.stringify(OperationsPickerConfigs))
    const selectedUnitSet = { id: 0 }
    const setAdvancedMode = (value: boolean) => {
        setOperationsPickerConfigsState(prev => ({ ...prev, advancedMode: value }))
    }

    let loadingSelectedUnitSet = false

    useEffect(() => {
        let newAssetName = getAssetName(
            dashboardContext?.params?.filter,
            widgetContext?.filters?.current?.lookupValues?.asset_name_event_type
        )
        if (!fetchingTree) setAsset(newAssetName)
    }, [
        JSON.stringify(dashboardContext),
        JSON.stringify(widgetContext?.filters?.current?.lookupValues?.asset_name_event_type)
    ])

    async function updateTree(refAsset) {
        let r = await getTreeUnformatted(asset, selectedUnitSet, loadingSelectedUnitSet, setFetchingTree, setError)
        if (r.assetName === refAsset.current) setFormattedTree(formatTree(r.tree, OperationsPickerConfigs))
    }

    useEffect(() => {
        if (!loadingSelectedUnitSet && (hasAssetChanged || haveConfigsChanged) && asset != '') {
            refAsset.current = asset
            setFormattedTree([])
            updateTree(refAsset)
        }
    }, [asset, selectedUnitSet, loadingSelectedUnitSet, JSON.stringify(OperationsPickerConfigs)])

    useEffect(() => {
        if (open && !innerOpen) setInnerOpen(true)
    }, [open])

    const innerToggleOpen = () => {
        if (innerOpen && open) toggleOpen()
        setInnerOpen(false)
    }

    return (
        <SpanPickerContext.Provider
            value={{
                operationsPickerConfigs: operationsPickerConfigsState,
                setOperationsPickerConfigsState: setOperationsPickerConfigsState,
                changeSpanPickerConfig: changeSpanPickerConfig,
                formattedTree: formattedTree,
                headerContext: {
                    open: innerOpen,
                    asset: asset,
                    toggleOpen: innerToggleOpen,
                    setTimespan: dashboardContext?.actions?.setTimespan ?? widgetContext?.actions?.setTimespan,
                    setFilters: dashboardContext?.actions?.setFilterValues ?? widgetContext?.actions?.testFilters,
                    updateFilter: widgetContext?.actions?.updateFilter,
                    filterValues: dashboardContext?.params?.filter ? JSON.parse(dashboardContext?.params?.filter) : {},
                    context: context,
                    hasAssetChange: hasAssetChanged,
                    advancedMode: operationsPickerConfigsState.advancedMode,
                    setAdvancedMode: setAdvancedMode
                }
            }}
        >   
            <SpanContainerStyled>
                {asset !== '' && !fetchingTree && formattedTree.length > 0 && !error ? (
                    <TreeKeys formattedTree={formattedTree} />
                ) : (
                    <LiveModal className={`modal ${operationsPickerConfigsState.advancedMode ? 'modal-sm' : 'modal-lg'}`} show={innerOpen}>
                        {!operationsPickerConfigsState.advancedMode ? (
                            <>
                                <LiveModal.Header>
                                    <LiveModal.Title>
                                        {i18n(
                                            operationsPickerConfigsState.advancedMode
                                                ? 'Select a timespan'
                                                : 'Select Drilling Run or Operation'
                                        )}
                                    </LiveModal.Title>
                                </LiveModal.Header>
                                <LiveModal.Body>
                                    <ModalBody>
                                        {asset === '' && i18n('One asset must be select to load its operations.')}
                                        {asset !== '' && fetchingTree && (
                                            <>
                                                {i18n('The operations are being loaded.')}
                                                <LiveLoading size="sm" show />
                                            </>
                                        )}
                                        {fetchingTree && error && i18n('There was an error while loading the operations.')}
                                        {!fetchingTree &&
                                            formattedTree?.length === 0 &&
                                            !error &&
                                            i18n("It wasn't found operations for this asset.")}
                                    </ModalBody>
                                </LiveModal.Body>
                                <LiveModal.Footer>
                                    <FooterActions>
                                        <a onClick={() => innerToggleOpen()}>{i18n('Cancel')}</a>
                                        <a
                                            className="mode-selector"
                                            onClick={() => setAdvancedMode(!operationsPickerConfigsState.advancedMode)}
                                        >
                                            {i18n('Advanced')}
                                        </a>
                                    </FooterActions>
                                </LiveModal.Footer>
                            </>
                        ) : (
                            <TimespanSelect />
                        )}
                    </LiveModal>
                )}
            </SpanContainerStyled>
        </SpanPickerContext.Provider>
    )
}

export default OperationsTreeInitializer
